import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Building2, Plus, Edit2, Trash2, RefreshCw, Check, X,
  Phone, Globe, Mail, FileText, Clock, TestTube,
  ChevronDown, ChevronRight, Settings, Download, Upload
} from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

async function listPayers() {
  const response = await fetch(`${API_URL}/api/payers/`);
  if (!response.ok) throw new Error('Failed to fetch payers');
  return response.json();
}

async function getPayerAnalytics() {
  const response = await fetch(`${API_URL}/api/payers/analytics`);
  if (!response.ok) throw new Error('Failed to fetch analytics');
  return response.json();
}

async function createPayer(data) {
  const response = await fetch(`${API_URL}/api/payers`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  if (!response.ok) throw new Error('Failed to create payer');
  return response.json();
}

async function updatePayer(id, data) {
  const response = await fetch(`${API_URL}/api/payers/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  if (!response.ok) throw new Error('Failed to update payer');
  return response.json();
}

async function deletePayer(id) {
  const response = await fetch(`${API_URL}/api/payers/${id}`, { method: 'DELETE' });
  if (!response.ok) throw new Error('Failed to delete payer');
  return response.json();
}

async function testPayerConfig(id, sampleText) {
  const response = await fetch(`${API_URL}/api/payers/${id}/test`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sample_text: sampleText })
  });
  if (!response.ok) throw new Error('Failed to test config');
  return response.json();
}

function PayerForm({ payer, onSave, onCancel }) {
  const [formData, setFormData] = useState(payer || {
    payer_name: '',
    payer_aliases: [],
    payer_id_patterns: [],
    is_active: true,
    extraction_config: {
      prompt_template: '',
      date_format: 'MM/DD/YYYY',
      currency_format: 'USD'
    },
    appeal_rules: {
      first_level_deadline_days: 180,
      second_level_deadline_days: 60,
      submission_method: 'portal',
      required_documents: []
    },
    contact_info: {
      phone: '',
      portal_url: '',
      appeal_address: '',
      customer_service_email: ''
    }
  });

  const [aliasesInput, setAliasesInput] = useState(formData.payer_aliases?.join(', ') || '');
  const [patternsInput, setPatternsInput] = useState(formData.payer_id_patterns?.join(', ') || '');

  const handleSubmit = () => {
    const data = {
      ...formData,
      payer_aliases: aliasesInput.split(',').map(s => s.trim()).filter(Boolean),
      payer_id_patterns: patternsInput.split(',').map(s => s.trim()).filter(Boolean)
    };
    onSave(data);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Payer Name *</Label>
          <Input
            value={formData.payer_name}
            onChange={(e) => setFormData({ ...formData, payer_name: e.target.value })}
            placeholder="e.g., Blue Cross Blue Shield"
          />
        </div>
        <div className="space-y-2">
          <Label>Active Status</Label>
          <div className="flex items-center space-x-2 pt-2">
            <Switch
              checked={formData.is_active}
              onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
            />
            <span className="text-sm">{formData.is_active ? 'Active' : 'Inactive'}</span>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Payer Aliases (comma-separated)</Label>
        <Input
          value={aliasesInput}
          onChange={(e) => setAliasesInput(e.target.value)}
          placeholder="BCBS, Blue Cross, Anthem BCBS"
        />
      </div>

      <div className="space-y-2">
        <Label>Detection Patterns (comma-separated regex)</Label>
        <Input
          value={patternsInput}
          onChange={(e) => setPatternsInput(e.target.value)}
          placeholder="Blue Cross, BCBS, Anthem"
        />
      </div>

      <div className="border-t pt-4">
        <h4 className="font-medium mb-3">Contact Information</h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Phone</Label>
            <Input
              value={formData.contact_info?.phone || ''}
              onChange={(e) => setFormData({
                ...formData,
                contact_info: { ...formData.contact_info, phone: e.target.value }
              })}
              placeholder="1-800-XXX-XXXX"
            />
          </div>
          <div className="space-y-2">
            <Label>Portal URL</Label>
            <Input
              value={formData.contact_info?.portal_url || ''}
              onChange={(e) => setFormData({
                ...formData,
                contact_info: { ...formData.contact_info, portal_url: e.target.value }
              })}
              placeholder="https://..."
            />
          </div>
          <div className="space-y-2 col-span-2">
            <Label>Appeal Address</Label>
            <Textarea
              value={formData.contact_info?.appeal_address || ''}
              onChange={(e) => setFormData({
                ...formData,
                contact_info: { ...formData.contact_info, appeal_address: e.target.value }
              })}
              placeholder="Appeals Department, PO Box..."
              rows={2}
            />
          </div>
        </div>
      </div>

      <div className="border-t pt-4">
        <h4 className="font-medium mb-3">Appeal Rules</h4>
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>First Level Deadline (days)</Label>
            <Input
              type="number"
              value={formData.appeal_rules?.first_level_deadline_days || 180}
              onChange={(e) => setFormData({
                ...formData,
                appeal_rules: { ...formData.appeal_rules, first_level_deadline_days: parseInt(e.target.value) }
              })}
            />
          </div>
          <div className="space-y-2">
            <Label>Second Level Deadline (days)</Label>
            <Input
              type="number"
              value={formData.appeal_rules?.second_level_deadline_days || 60}
              onChange={(e) => setFormData({
                ...formData,
                appeal_rules: { ...formData.appeal_rules, second_level_deadline_days: parseInt(e.target.value) }
              })}
            />
          </div>
          <div className="space-y-2">
            <Label>Submission Method</Label>
            <Input
              value={formData.appeal_rules?.submission_method || 'portal'}
              onChange={(e) => setFormData({
                ...formData,
                appeal_rules: { ...formData.appeal_rules, submission_method: e.target.value }
              })}
              placeholder="portal, mail, fax"
            />
          </div>
        </div>
      </div>

      <div className="border-t pt-4">
        <h4 className="font-medium mb-3">Extraction Configuration</h4>
        <div className="space-y-2">
          <Label>Custom Prompt Template</Label>
          <Textarea
            value={formData.extraction_config?.prompt_template || ''}
            onChange={(e) => setFormData({
              ...formData,
              extraction_config: { ...formData.extraction_config, prompt_template: e.target.value }
            })}
            placeholder="Additional extraction instructions for this payer..."
            rows={3}
          />
        </div>
      </div>

      <div className="flex justify-end space-x-2 pt-4 border-t">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={handleSubmit}>Save Payer</Button>
      </div>
    </div>
  );
}

function PayerAnalyticsCard({ analytics }) {
  if (!analytics || analytics.length === 0) {
    return <p className="text-sm text-muted-foreground text-center py-8">No analytics data available</p>;
  }

  return (
    <div className="space-y-4">
      {analytics.map((payer, idx) => (
        <div key={idx} className="p-4 border rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium">{payer._id}</h4>
            <Badge variant="outline">{payer.total_documents} docs</Badge>
          </div>
          <div className="grid grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-xs text-muted-foreground">Revenue</p>
              <p className="font-medium">${(payer.total_amount || 0).toFixed(2)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Denial Rate</p>
              <p className="font-medium">{(payer.denial_rate || 0).toFixed(1)}%</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Avg Confidence</p>
              <p className="font-medium">{((payer.avg_confidence || 0) * 100).toFixed(0)}%</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Avg Time</p>
              <p className="font-medium">{(payer.avg_processing_time || 0).toFixed(0)}s</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function PayerManagementPage() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingPayer, setEditingPayer] = useState(null);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testPayerId, setTestPayerId] = useState(null);
  const [testSampleText, setTestSampleText] = useState('');
  const [testResult, setTestResult] = useState(null);

  const { data: payersData, isLoading } = useQuery({
    queryKey: ['payers'],
    queryFn: listPayers,
  });

  const { data: analytics } = useQuery({
    queryKey: ['payer-analytics'],
    queryFn: getPayerAnalytics,
  });

  const createMutation = useMutation({
    mutationFn: createPayer,
    onSuccess: () => {
      toast.success('Payer created successfully!');
      queryClient.invalidateQueries(['payers']);
      setShowForm(false);
    },
    onError: (error) => toast.error(`Failed to create payer: ${error.message}`)
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => updatePayer(id, data),
    onSuccess: () => {
      toast.success('Payer updated successfully!');
      queryClient.invalidateQueries(['payers']);
      setEditingPayer(null);
    },
    onError: (error) => toast.error(`Failed to update payer: ${error.message}`)
  });

  const deleteMutation = useMutation({
    mutationFn: deletePayer,
    onSuccess: () => {
      toast.success('Payer deactivated successfully!');
      queryClient.invalidateQueries(['payers']);
    },
    onError: (error) => toast.error(`Failed to delete payer: ${error.message}`)
  });

  const testMutation = useMutation({
    mutationFn: ({ id, text }) => testPayerConfig(id, text),
    onSuccess: (data) => {
      setTestResult(data);
    },
    onError: (error) => toast.error(`Test failed: ${error.message}`)
  });

  const payers = payersData?.configs || [];

  const handleSave = (data) => {
    if (editingPayer) {
      updateMutation.mutate({ id: editingPayer.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleTest = () => {
    if (testPayerId && testSampleText) {
      testMutation.mutate({ id: testPayerId, text: testSampleText });
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-48"></div>
          <div className="h-64 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6" data-testid="payer-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold mb-2">Payer Management</h1>
          <p className="text-muted-foreground">Configure payer templates for EOB processing</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" onClick={() => queryClient.invalidateQueries(['payers'])}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => { setEditingPayer(null); setShowForm(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            Add Payer
          </Button>
        </div>
      </div>

      {/* Form Dialog */}
      {(showForm || editingPayer) && (
        <Card>
          <CardHeader>
            <CardTitle>{editingPayer ? 'Edit Payer' : 'Add New Payer'}</CardTitle>
            <CardDescription>
              Configure payer-specific extraction and appeal settings
            </CardDescription>
          </CardHeader>
          <CardContent>
            <PayerForm
              payer={editingPayer}
              onSave={handleSave}
              onCancel={() => { setShowForm(false); setEditingPayer(null); }}
            />
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="payers" className="w-full">
        <TabsList>
          <TabsTrigger value="payers">Payer Configurations ({payers.length})</TabsTrigger>
          <TabsTrigger value="analytics">Payer Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="payers" className="mt-4">
          <Card>
            <CardContent className="pt-6">
              {payers.length === 0 ? (
                <div className="text-center py-12">
                  <Building2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground mb-4">No payer configurations found</p>
                  <Button onClick={() => setShowForm(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add First Payer
                  </Button>
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead>Payer Name</TableHead>
                        <TableHead>Aliases</TableHead>
                        <TableHead>First Level Deadline</TableHead>
                        <TableHead>Submission Method</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {payers.map((payer) => (
                        <TableRow key={payer.id}>
                          <TableCell className="font-medium">
                            <div className="flex items-center space-x-2">
                              <Building2 className="w-4 h-4 text-muted-foreground" />
                              <span>{payer.payer_name}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {payer.payer_aliases?.slice(0, 3).map((alias, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs">
                                  {alias}
                                </Badge>
                              ))}
                              {payer.payer_aliases?.length > 3 && (
                                <Badge variant="outline" className="text-xs">
                                  +{payer.payer_aliases.length - 3}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {payer.appeal_rules?.first_level_deadline_days || 180} days
                          </TableCell>
                          <TableCell className="capitalize">
                            {payer.appeal_rules?.submission_method || 'portal'}
                          </TableCell>
                          <TableCell>
                            <Badge className={payer.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                              {payer.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center space-x-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  setTestPayerId(payer.id);
                                  setTestResult(null);
                                  setTestDialogOpen(true);
                                }}
                              >
                                <TestTube className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setEditingPayer(payer)}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => deleteMutation.mutate(payer.id)}
                              >
                                <Trash2 className="w-4 h-4 text-destructive" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analytics" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Payer Performance Analytics</CardTitle>
              <CardDescription>Processing metrics by payer</CardDescription>
            </CardHeader>
            <CardContent>
              <PayerAnalyticsCard analytics={analytics} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Test Dialog */}
      <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Test Payer Detection</DialogTitle>
            <DialogDescription>
              Enter sample EOB text to test payer detection accuracy
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              value={testSampleText}
              onChange={(e) => setTestSampleText(e.target.value)}
              placeholder="Paste sample EOB text containing payer information..."
              rows={5}
            />
            {testResult && (
              <div className={`p-4 rounded-lg ${testResult.is_match ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'} border`}>
                <div className="flex items-center space-x-2 mb-2">
                  {testResult.is_match ? (
                    <Check className="w-5 h-5 text-green-600" />
                  ) : (
                    <X className="w-5 h-5 text-red-600" />
                  )}
                  <span className="font-medium">
                    {testResult.is_match ? 'Detection Successful' : 'Detection Failed'}
                  </span>
                </div>
                <p className="text-sm">Detected: {testResult.detected_payer || 'None'}</p>
                <p className="text-sm">Confidence: {((testResult.detection_confidence || 0) * 100).toFixed(0)}%</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestDialogOpen(false)}>Close</Button>
            <Button onClick={handleTest} disabled={testMutation.isPending || !testSampleText}>
              {testMutation.isPending ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <TestTube className="w-4 h-4 mr-2" />
              )}
              Run Test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
